import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormLabel,
  Typography,
} from "@mui/material";
import DOMPurify from "dompurify";
import { FC, ReactElement, useMemo } from "react";

import __ from "web/Layout/Translations";

import formatDate from "web/utils/data/parser/dateAndTime/formatDate";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import {
  ContentTypeActiveFormData,
  HeaderTypeActiveFormData,
  ISubscriptionFormData,
  SubscriptionActiveFormData,
  SubscriptionActiveFormDataType,
  SubscriptionActiveFormField,
  SubscriptionActiveFormFieldType,
} from "web/types/Subscription";

interface IActiveFormPreviewProps {
  configuration: SubscriptionActiveFormData[];
  formData: ISubscriptionFormData;
  isMobile: boolean;
  closeHandler: () => void;
}

const ActiveFormPreviewMUI: FC<IActiveFormPreviewProps> = ({
  configuration,
  formData,
  isMobile,
  closeHandler,
}) => {
  const getHeader = (header: HeaderTypeActiveFormData) => {
    return (
      <Typography variant="subtitle1" key={header.id} mb={1}>
        {header.value}
      </Typography>
    );
  };

  const getContent = (content: ContentTypeActiveFormData) => {
    return (
      <Box key={content.id} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" mb={1}>
          {content.name}
        </Typography>
        {content.useCheckbox ? (
          <FormControlLabel
            control={
              <Checkbox
                checked
                disabled
                sx={{ color: "#00000061" }}
                size="medium"
              />
            }
            label={
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(content.content),
                }}
                style={{ color: "#999", fontSize: 16, lineHeight: 1.5 }}
              />
            }
            sx={{
              alignItems: "flex-start",
              m: 0,
            }}
          />
        ) : (
          <Box
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(content.content),
            }}
            sx={{ fontSize: 16, lineHeight: 1.5 }}
          />
        )}
      </Box>
    );
  };

  const getField = (
    field: SubscriptionActiveFormField,
    multiValueType?: string
  ): ReactElement => {
    const { fields } = formData;
    const { id } = field;

    let value = multiValueType
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        fields[id][multiValueType]
      : fields[id];

    if (
      field.type === SubscriptionActiveFormFieldType.DATE ||
      field.type === SubscriptionActiveFormFieldType.BIRTHDATE
    ) {
      value = formatDate(value) || "";
    }
    if (field.type === SubscriptionActiveFormFieldType.REQUIRE_PESEL) {
      return (
        <>
          {getField(
            {
              type: SubscriptionActiveFormFieldType.CHECKBOX,
              id,
              label: __("Obcokrajowiec / nowonarodzone dziecko"),
            },
            "babyOrForeigner"
          )}
          {getField(
            {
              type: SubscriptionActiveFormFieldType.PESEL,
              id,
              label: __("Pesel"),
            },
            SubscriptionActiveFormFieldType.PESEL
          )}
          {getField(
            {
              type: SubscriptionActiveFormFieldType.BIRTHDATE,
              id,
              label: __("Data urodzenia"),
            },
            SubscriptionActiveFormFieldType.BIRTHDATE
          )}
        </>
      );
    }
    if (field.type === SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX) {
      const fieldValue = fields[id];
      let formattedPhone = "-";

      if (fieldValue && typeof fieldValue === "object") {
        const phoneData = fieldValue as {
          prefix?: string;
          phoneNumber?: string;
        };
        formattedPhone =
          `${phoneData.prefix || ""} ${phoneData.phoneNumber || ""}`.trim() ||
          "-";
      } else if (typeof fieldValue === "string" && fieldValue) {
        formattedPhone = fieldValue;
      }

      return (
        <Box key={id} sx={{ display: "flex" }} mb={0.5}>
          <Typography variant="body2" mr={0.5} sx={{ whiteSpace: "nowrap" }}>
            {field.label}:
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ whiteSpace: "normal", wordBreak: "break-all" }}
          >
            {formattedPhone}
          </Typography>
        </Box>
      );
    }
    if (field.type === SubscriptionActiveFormFieldType.CHECKBOX) {
      return (
        <Box key={id} display="flex" alignItems="center" sx={{ mb: 0.5 }}>
          <Checkbox
            checked={Boolean(value)}
            disabled
            sx={{ color: "#00000061" }}
            size="medium"
          />
          <FormLabel disabled sx={{ m: 0 }}>
            {field.label}
          </FormLabel>
        </Box>
      );
    }

    if (field.type === SubscriptionActiveFormFieldType.SELECT) {
      value =
        field?.options?.find((el) => el.value === fields[id])?.label ||
        fields[id];
    }

    const currentLabel = field.type === "select" ? field.title : field.label;

    return (
      <Box sx={{ display: "flex" }} mb={0.5}>
        <Typography variant="body2" mr={0.5} sx={{ whiteSpace: "nowrap" }}>
          {currentLabel}:
        </Typography>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ whiteSpace: "normal", wordBreak: "break-all" }}
        >
          {value || "-"}
        </Typography>
      </Box>
    );
  };

  const formBody = useMemo(() => {
    if (!isArrayHasItems(configuration)) return null;

    return configuration.map((section) => {
      switch (section.type) {
        case SubscriptionActiveFormDataType.HEADER:
          return getHeader(section);
        case SubscriptionActiveFormDataType.CONTENT:
          return getContent(section);
        case SubscriptionActiveFormDataType.FORM:
          return (
            <>
              {section.fields.map((key) => getField(key))}
              <Divider sx={{ my: 3 }} />
            </>
          );
        default:
          return null;
      }
    });
  }, [configuration]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        ...(isMobile && {
          px: 2,
          pt: 3,
          backgroundColor: "#fff",
          borderTopRightRadius: "16px",
          borderTopLeftRadius: "16px",
          boxShadow:
            "0px 1px 1px #F2F2F2, 0px 4px 24px rgba(166, 166, 166, 0.2)",
        }),
      }}
    >
      <Box px={isMobile ? 0 : 2}>{formBody}</Box>
      <Box display="flex" justifyContent="flex-end" sx={{ pb: 2 }}>
        <Button
          variant="outlined"
          size="large"
          color="inherit"
          sx={{
            borderColor: "grey.400",
          }}
          onClick={closeHandler}
          data-t1="button_back"
          fullWidth={isMobile}
        >
          <ChevronLeftIcon sx={{ mr: "8px" }} />
          {__("Wróć")}
        </Button>
      </Box>
    </Box>
  );
};

export default ActiveFormPreviewMUI;
